import React, { forwardRef, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import axios from 'axios';
import { Capacitor } from '@capacitor/core';
import {
    errorMessage,
    getTeamsBySport,
    getUserSports,
    isValidReturnUrl,
    successMessage,
} from '../utils/helpers';
import config from '../utils/config';
import nativeStorage from '../utils/nativeStorage';
import BronzeStatueShowcase from './animation/BronzeStatueShowcase';
import { Button } from './design-system';
import { C } from './design-system/primitives/constants';

const IS_NATIVE = Capacitor.isNativePlatform();

// Google Client ID - use environment variable with fallback
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID ||
  '359065791766-f1hunj8q731vgct1d89etsv4sef50cfa.apps.googleusercontent.com';
const IS_LOCALHOST = typeof window !== 'undefined' &&
    (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
const IS_USING_GOOGLE_CLIENT_FALLBACK = !process.env.REACT_APP_GOOGLE_CLIENT_ID;

// UNC accepts username or email for team domain accounts
const ACCEPTS_USERNAME_LOGIN = true;

const unwrapFirstValue = (value) => {
    while (Array.isArray(value) && value.length > 0) value = value[0];
    return value;
};

const normalizeUserForSession = (user) => {
    const normalized = {
        ...user,
        sport_type: unwrapFirstValue(user?.sport_type) || null,
        team_type: unwrapFirstValue(user?.team_type) || null,
        university_team: unwrapFirstValue(user?.university_team) || null,
    };
    // UNC instance always forces baseball
    normalized.sport_type = 'baseball';
    return normalized;
};

/**
 * Login Component — UNC Tarheels Baseball Intelligence
 *
 * Single-flow layout: bronze statue video background + glass auth card.
 * Google SSO via hidden GSI button + custom SLAM-branded trigger.
 * Email/password with username support for team domain accounts.
 */
const Login = forwardRef((props, _ref) => {
    const { setShowLogo = () => { } } = props;
    const navigate = useNavigate();
    const location = useLocation();
    const [spin, setSpin] = useState(false);
    const [isTransitioning, setIsTransitioning] = useState(false);
    const [showEntrance, setShowEntrance] = useState(false);

    // Google Sign-In state
    const [googleInitialized, setGoogleInitialized] = useState(false);
    const [googleError, setGoogleError] = useState(null);
    const googleCallbackRef = useRef(null);

    // Email/password state
    const [authMode, setAuthMode] = useState('signin'); // 'signin' | 'signup'
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [name, setName] = useState('');
    const [formError, setFormError] = useState(null);

    useEffect(() => {
        const timer = setTimeout(() => setShowEntrance(true), 100);
        return () => clearTimeout(timer);
    }, []);

    const clearStoredRedirect = () => {
        sessionStorage.removeItem('redirectAfterLogin');
        localStorage.removeItem('redirectAfterLogin');
    };

    const persistFallbackSelections = (user) => {
        if (user?.sport_type) {
            nativeStorage.setItem('selectedSport', JSON.stringify({
                code: user.sport_type,
                name: user.sport_type,
            }));
            localStorage.setItem('slam_sport_selected', 'true');
            localStorage.setItem('slam_sport_selection', user.sport_type);
        }
        if (user?.team_type || user?.university_team) {
            const fallbackTeamId = user.team_type || user.university_team;
            const fallbackTeamName = user.university_team || user.team_type;
            nativeStorage.setItem('selectedTeam', JSON.stringify({
                team_id: fallbackTeamId,
                teamID: fallbackTeamId,
                name: fallbackTeamName,
                team_name: fallbackTeamName,
            }));
        }
    };

    const getDefaultDestination = (user) => {
        const sport = unwrapFirstValue(user?.sport_type);
        if (sport) return `/${sport}/gm`;
        const savedSport = localStorage.getItem('slam_sport_selection');
        if (savedSport) return `/${savedSport}/gm`;
        return '/baseball/gm';
    };

    const getRedirectTarget = (user) => {
        const params = new URLSearchParams(location.search);
        const queryReturnUrl = params.get('returnUrl');
        const sessionRedirect = sessionStorage.getItem('redirectAfterLogin');
        const localRedirect = localStorage.getItem('redirectAfterLogin');

        const requestedRedirect = [queryReturnUrl, sessionRedirect, localRedirect]
            .find((candidate) => isValidReturnUrl(candidate));

        return requestedRedirect || getDefaultDestination(user);
    };

    const prefetchUserContext = async (user) => {
        if (!user?.id || !user?.sport_type || !user?.team_type) return;
        try {
            const [sportResult, teamResult] = await Promise.all([
                getUserSports(user.id, user.sport_type),
                getTeamsBySport(user.id, user.sport_type, user.team_type),
            ]);
            if (teamResult?.teams) nativeStorage.setItem('selectedTeam', JSON.stringify(teamResult.teams));
            if (sportResult?.sports) nativeStorage.setItem('selectedSport', JSON.stringify(sportResult.sports));
        } catch (prefetchError) {
            console.warn('Team/sport prefetch failed (non-blocking):', prefetchError?.message);
        }
    };

    // Handle successful login — shared by Google SSO and email/password
    const handleLoginSuccess = async (user) => {
        const normalizedUser = normalizeUserForSession(user);

        nativeStorage.setItem('user', JSON.stringify(normalizedUser));
        persistFallbackSelections(normalizedUser);
        setShowLogo(true);
        setIsTransitioning(true);
        successMessage('You have successfully logged in!');

        if (!normalizedUser?.sport_type) {
            localStorage.removeItem('slam_sport_selected');
            localStorage.removeItem('slam_sport_selection');
        }

        const redirectTo = getRedirectTarget(normalizedUser);
        clearStoredRedirect();

        // Prefetch in background — don't block login completion
        void prefetchUserContext(normalizedUser);

        navigate(redirectTo, { replace: true });
    };

    // Send social login to backend (shared by web GSI and native plugin)
    const postSocialLogin = async ({ email: socialEmail, name: socialName, socialId, token }) => {
        setSpin(true);
        try {
            const res = await axios.post(`${config.SERVER_BASE_URL}/social-login`, {
                social_id: socialId,
                email: socialEmail?.toLowerCase(),
                name: socialName,
                token: token || 'native',
            });

            if (res.data?.success) {
                const user = res.data.data?.user || res.data.user;
                await handleLoginSuccess(user);
            } else {
                errorMessage(res.data?.message || 'Login failed');
            }
        } catch (err) {
            errorMessage(err?.response?.data?.message || 'Google Sign-In failed');
        } finally {
            setSpin(false);
        }
    };

    // Native Google Sign-In (Capacitor plugin)
    const onNativeGoogleSignIn = async () => {
        try {
            const { GoogleAuth } = await import('@codetrix-studio/capacitor-google-auth');
            await GoogleAuth.initialize({
                clientId: GOOGLE_CLIENT_ID,
                scopes: ['profile', 'email'],
            });
            const result = await GoogleAuth.signIn();
            await postSocialLogin({
                email: result.email,
                name: result.name || result.givenName,
                socialId: result.id,
                token: result.authentication?.idToken,
            });
        } catch (err) {
            if (err?.message?.includes('canceled') || err?.message?.includes('cancelled')) return;
            console.error('Native Google Sign-In error:', err);
            errorMessage('Google Sign-In failed. Please try again.');
        }
    };

    // Initialize Google Sign-In (web only)
    useEffect(() => {
        if (IS_NATIVE) {
            setGoogleInitialized(true);
            return;
        }

        let isMounted = true;

        googleCallbackRef.current = async (response) => {
            const decoded = jwtDecode(response.credential);
            await postSocialLogin({
                email: decoded.email,
                name: decoded.name,
                socialId: decoded.sub,
                token: response.credential,
            });
        };

        const initGoogleSignIn = () => {
            if (!window.google?.accounts?.id) {
                setTimeout(initGoogleSignIn, 500);
                return;
            }
            if (!isMounted) return;

            try {
                window.google.accounts.id.initialize({
                    client_id: GOOGLE_CLIENT_ID,
                    callback: (resp) => googleCallbackRef.current?.(resp),
                    use_fedcm_for_prompt: false,
                    auto_select: false,
                });

                // Render Google's real button into the transparent overlay
                const overlayDiv = document.getElementById('googleBtnOverlay');
                if (overlayDiv) {
                    while (overlayDiv.firstChild) overlayDiv.removeChild(overlayDiv.firstChild);
                    window.google.accounts.id.renderButton(overlayDiv, {
                        theme: 'outline',
                        size: 'large',
                        type: 'standard',
                        width: 400,
                    });
                }

                setGoogleInitialized(true);
                setGoogleError(null);
            } catch (err) {
                console.error('Google Sign-In init error:', err);
                if (isMounted) setGoogleError('Failed to initialize Google Sign-In');
            }
        };

        const timer = setTimeout(initGoogleSignIn, 300);
        return () => { isMounted = false; clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [navigate]);

    const onLocalDevLogin = async () => {
        setSpin(true);
        try {
            const res = await axios.post(`${config.SERVER_BASE_URL}/social-login`, {
                social_id: `local-dev-${Date.now()}`,
                email: 'local.dev@localhost',
                name: 'Local Dev',
                token: 'local-dev',
            });
            if (res.data?.success) {
                await handleLoginSuccess(res.data.data?.user || res.data.user);
            } else {
                errorMessage(res.data?.message || 'Local login failed');
            }
        } catch (err) {
            errorMessage(err?.response?.data?.message || 'Local login failed');
        } finally {
            setSpin(false);
        }
    };

    const onEmailSubmit = async (e) => {
        e.preventDefault();
        setFormError(null);
        if (!email || !password) {
            setFormError('Please enter email and password.');
            return;
        }
        if (authMode === 'signup' && password.length < 6) {
            setFormError('Password must be at least 6 characters.');
            return;
        }
        setSpin(true);
        try {
            const endpoint = authMode === 'signup' ? '/signup' : '/login';
            const body = authMode === 'signup'
                ? { email: email.toLowerCase(), password, name: name || undefined }
                : { email: email.toLowerCase(), password };
            const res = await axios.post(`${config.SERVER_BASE_URL}${endpoint}`, body);

            if (res.data?.success) {
                await handleLoginSuccess(res.data.data?.user || res.data.user);
            } else {
                setFormError(res.data?.message || `${authMode === 'signup' ? 'Signup' : 'Login'} failed`);
            }
        } catch (err) {
            setFormError(err?.response?.data?.message || `${authMode === 'signup' ? 'Signup' : 'Login'} failed`);
        } finally {
            setSpin(false);
        }
    };

    const styles = {
        container: {
            position: 'relative',
            background: C.bg,
            minHeight: '100vh',
        },
        transitionOverlay: {
            position: 'fixed', inset: 0, background: C.bg, zIndex: 100,
            opacity: isTransitioning ? 1 : 0,
            pointerEvents: isTransitioning ? 'all' : 'none',
            transition: 'opacity 0.6s ease-in-out',
        },
        glassCard: {
            background: `${C.s1}CC`,
            backdropFilter: 'blur(14px)', WebkitBackdropFilter: 'blur(14px)',
            border: `1px solid ${C.border}`,
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.45), inset 0 1px 0 rgba(255, 255, 255, 0.03)',
            borderRadius: '18px',
        },
        logoGlow: {
            filter: `drop-shadow(0 0 20px ${C.gold}66) drop-shadow(0 0 40px ${C.gold}33)`,
            animation: 'pulse-glow 3s ease-in-out infinite',
        },
        backgroundScrim: {
            position: 'absolute', inset: 0, zIndex: 1,
            background: 'linear-gradient(90deg, rgba(0,0,0,0.84) 0%, rgba(0,0,0,0.7) 38%, rgba(0,0,0,0.88) 100%)',
        },
        vignette: {
            position: 'absolute', inset: 0, zIndex: 2,
            background: `radial-gradient(circle at 50% 18%, ${C.g15} 0%, rgba(0,0,0,0) 36%), radial-gradient(circle at 50% 100%, rgba(0,0,0,0) 40%, rgba(0,0,0,0.94) 100%)`,
        },
        cardLoadingOverlay: {
            position: 'absolute', inset: 0, zIndex: 2,
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            background: 'rgba(0,0,0,0.5)',
            backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)',
        },
    };

    // ── Google Sign-In Button ──
    // Web: overlay pattern — real Google iframe sits transparently on top of our styled button.
    // User clicks the invisible real iframe, which triggers Google's OAuth popup.
    // Native: plain button that calls Capacitor Google Auth plugin.
    const GoogleButton = IS_NATIVE ? (
        <button
            type="button"
            onClick={onNativeGoogleSignIn}
            disabled={spin}
            className="w-full flex items-center justify-center gap-3 py-3 rounded-lg text-sm font-medium transition-all disabled:opacity-50"
            style={{ background: C.s2, border: `1px solid ${C.border}`, color: C.text }}
        >
            <GoogleLogo />
            <span>Continue with Google</span>
        </button>
    ) : (
        <div className="relative w-full" style={{ height: 48 }}>
            {/* Visible styled button (underneath) */}
            <div
                className="absolute inset-0 flex items-center justify-center gap-3 rounded-lg text-sm font-medium"
                style={{ background: C.s2, border: `1px solid ${C.border}`, color: C.text }}
            >
                <GoogleLogo />
                <span>{googleInitialized ? 'Continue with Google' : 'Loading Google Sign-In...'}</span>
            </div>
            {/* Real Google button overlay (transparent, captures click) */}
            <div
                id="googleBtnOverlay"
                className="absolute inset-0 flex items-center justify-center overflow-hidden rounded-lg"
                style={{ opacity: 0.01, cursor: 'pointer', zIndex: 1 }}
            />
        </div>
    );

    return (
        <>
            <style>{`
                @keyframes pulse-glow {
                    0%, 100% { filter: drop-shadow(0 0 20px ${C.gold}66) drop-shadow(0 0 40px ${C.gold}33); }
                    50% { filter: drop-shadow(0 0 30px ${C.gold}99) drop-shadow(0 0 60px ${C.gold}4D); }
                }
            `}</style>

            {/* Transition overlay */}
            <div style={styles.transitionOverlay}>
                <div className="flex items-center justify-center h-full">
                    <img src="/SLAM_logo_login.png" alt="SLAM" className="w-40 animate-pulse" style={styles.logoGlow} />
                </div>
            </div>

            <div className="min-h-screen overflow-x-hidden overflow-y-auto" style={styles.container}>
                {/* Background: bronze statue video carousel */}
                <div className="pointer-events-none absolute inset-0">
                    <BronzeStatueShowcase />
                </div>
                <div style={styles.backgroundScrim} />
                <div style={styles.vignette} />

                {/* Foreground: two-column layout */}
                <div className="relative z-10 min-h-screen px-4 py-6 sm:px-8 sm:py-10">
                    <div className="mx-auto flex min-h-[calc(100vh-3rem)] w-full max-w-6xl items-center justify-center">
                        <div className="grid w-full items-center gap-8 lg:grid-cols-[minmax(0,1.15fr)_minmax(360px,420px)]">

                            {/* ── Left column: Hero branding (desktop only) ── */}
                            <div
                                className="hidden lg:block"
                                style={{
                                    opacity: showEntrance ? 1 : 0,
                                    transform: showEntrance ? 'translateY(0)' : 'translateY(24px)',
                                    transition: 'opacity 0.8s ease, transform 0.8s ease',
                                }}
                            >
                                <div
                                    className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-[11px] uppercase tracking-[0.28em]"
                                    style={{ background: C.g08, border: `1px solid ${C.g15}`, color: C.gold }}
                                >
                                    UNC Baseball Intelligence
                                </div>

                                <blockquote
                                    className="mt-6 max-w-xl text-4xl font-light leading-[1.12] tracking-[-0.03em]"
                                    style={{ color: C.text }}
                                >
                                    &ldquo;A team that sacrifices the &lsquo;me&rsquo; for the &lsquo;we&rsquo; is a true team.&rdquo;
                                </blockquote>
                                <p
                                    className="mt-4 text-sm tracking-wide"
                                    style={{ color: C.gold, fontWeight: 500 }}
                                >
                                    &mdash; Scott Forbes, Head Coach
                                </p>

                                <p
                                    className="mt-5 max-w-xl text-base leading-7"
                                    style={{ color: 'rgba(245, 245, 245, 0.72)' }}
                                >
                                    Film, roster, scouting, and player intel in a single workspace.
                                    Built for the Diamond Heels coaching staff.
                                </p>
                                <div
                                    className="mt-8 h-px w-32"
                                    style={{ background: `linear-gradient(90deg, ${C.gold} 0%, transparent 100%)` }}
                                />
                            </div>

                            {/* ── Right column: Auth card ── */}
                            <div
                                className="mx-auto w-full max-w-md"
                                style={{
                                    opacity: showEntrance ? 1 : 0,
                                    transform: showEntrance ? 'translateY(0)' : 'translateY(24px)',
                                    transition: 'opacity 0.8s ease 0.08s, transform 0.8s ease 0.08s',
                                }}
                            >
                                {/* Logo */}
                                <div className="text-center mb-6 sm:mb-8">
                                    <div className="flex justify-center mb-6">
                                        <img src="/SLAM_logo_login.png" alt="SLAM" className="w-32 sm:w-36" style={styles.logoGlow} />
                                    </div>
                                    <h1 style={{ fontSize: 24, fontWeight: 200, letterSpacing: 2, color: C.gold, marginBottom: 4 }}>
                                        SLAM SPORTS AI
                                    </h1>
                                    <p style={{ fontSize: 13, color: 'rgba(245, 245, 245, 0.72)', letterSpacing: 1 }}>
                                        Advanced Athletic Intelligence Platform
                                    </p>
                                    <p style={{ fontSize: 11, color: C.gold, letterSpacing: 2, marginTop: 8, fontWeight: 400, textTransform: 'uppercase' }}>
                                        UNC Tarheels Baseball
                                    </p>
                                </div>

                                {/* Auth card */}
                                <div style={styles.glassCard} className="relative overflow-hidden p-6 sm:p-7">
                                    {spin && (
                                        <div style={styles.cardLoadingOverlay}>
                                            <div
                                                className="h-9 w-9 animate-spin rounded-full border-2 border-transparent"
                                                style={{ borderTopColor: C.gold, borderRightColor: `${C.gold}66` }}
                                            />
                                            <p className="mt-4 text-sm tracking-[0.24em] uppercase" style={{ color: C.gold }}>
                                                Signing in
                                            </p>
                                        </div>
                                    )}

                                    <div
                                        className="space-y-4"
                                        style={{
                                            opacity: spin ? 0.42 : 1,
                                            pointerEvents: spin ? 'none' : 'auto',
                                            transition: 'opacity 0.2s ease',
                                        }}
                                    >
                                        {/* Google Sign-In */}
                                        {GoogleButton}

                                        {!IS_NATIVE && !googleInitialized && !googleError && (
                                            <p className="text-center text-xs" style={{ color: 'rgba(245, 245, 245, 0.62)' }}>
                                                Loading Google Sign-In...
                                            </p>
                                        )}
                                        {googleError && (
                                            <p className="text-center text-xs text-red-400">{googleError}</p>
                                        )}

                                        {/* Divider */}
                                        <div className="flex items-center gap-3">
                                            <div className="flex-1 h-px" style={{ background: C.border }} />
                                            <span className="text-xs" style={{ color: C.t4 }}>or</span>
                                            <div className="flex-1 h-px" style={{ background: C.border }} />
                                        </div>

                                        {/* Email/Password Form */}
                                        <form onSubmit={onEmailSubmit} className="space-y-3">
                                            {authMode === 'signup' && (
                                                <input
                                                    type="text"
                                                    placeholder="Name (optional)"
                                                    value={name}
                                                    onChange={(e) => setName(e.target.value)}
                                                    className="w-full px-3 py-2.5 rounded-lg text-sm outline-none transition-colors"
                                                    style={{ background: C.s2, border: `1px solid ${C.border}`, color: C.text }}
                                                    autoComplete="name"
                                                />
                                            )}
                                            <input
                                                type={ACCEPTS_USERNAME_LOGIN ? 'text' : 'email'}
                                                placeholder={ACCEPTS_USERNAME_LOGIN ? 'Username or Email' : 'Email'}
                                                value={email}
                                                onChange={(e) => setEmail(e.target.value)}
                                                required
                                                className="w-full px-3 py-2.5 rounded-lg text-sm outline-none transition-colors"
                                                style={{ background: C.s2, border: `1px solid ${C.border}`, color: C.text }}
                                                autoComplete={ACCEPTS_USERNAME_LOGIN ? 'username' : 'email'}
                                            />
                                            <input
                                                type="password"
                                                placeholder="Password"
                                                value={password}
                                                onChange={(e) => setPassword(e.target.value)}
                                                required
                                                minLength={authMode === 'signup' ? 6 : undefined}
                                                className="w-full px-3 py-2.5 rounded-lg text-sm outline-none transition-colors"
                                                style={{ background: C.s2, border: `1px solid ${C.border}`, color: C.text }}
                                                autoComplete={authMode === 'signup' ? 'new-password' : 'current-password'}
                                            />
                                            {formError && (
                                                <p className="text-xs text-red-400">{formError}</p>
                                            )}
                                            <button
                                                type="submit"
                                                className="w-full text-sm font-medium py-3 rounded-lg transition-colors disabled:opacity-50"
                                                disabled={spin}
                                                style={{ background: C.gold, color: '#000' }}
                                            >
                                                {authMode === 'signup' ? 'Create Account' : 'Sign In'}
                                            </button>
                                        </form>

                                        {/* Toggle sign-in / sign-up */}
                                        <p className="text-center text-xs" style={{ color: 'rgba(245, 245, 245, 0.62)' }}>
                                            {authMode === 'signin' ? (
                                                <>
                                                    Don&apos;t have an account?{' '}
                                                    <button
                                                        type="button"
                                                        onClick={() => { setAuthMode('signup'); setFormError(null); }}
                                                        className="underline hover:opacity-80"
                                                        style={{ color: C.gold }}
                                                    >
                                                        Sign up
                                                    </button>
                                                </>
                                            ) : (
                                                <>
                                                    Already have an account?{' '}
                                                    <button
                                                        type="button"
                                                        onClick={() => { setAuthMode('signin'); setFormError(null); }}
                                                        className="underline hover:opacity-80"
                                                        style={{ color: C.gold }}
                                                    >
                                                        Sign in
                                                    </button>
                                                </>
                                            )}
                                        </p>

                                        {/* Local dev bypass */}
                                        {IS_LOCALHOST && IS_USING_GOOGLE_CLIENT_FALLBACK && (
                                            <div className="pt-2 space-y-2">
                                                <div className="border-t" style={{ borderColor: C.border }} />
                                                <p className="text-center text-xs pt-2" style={{ color: C.t4 }}>
                                                    Local dev: set <code style={{ color: C.gold }}>REACT_APP_GOOGLE_CLIENT_ID</code> for Google SSO
                                                </p>
                                                <div className="flex justify-center">
                                                    <Button type="button" variant="outline" className="text-xs" onClick={onLocalDevLogin} disabled={spin}>
                                                        Continue With Local Dev Login
                                                    </Button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Footer */}
                                <div className="mt-6 text-center">
                                    <p className="text-xs" style={{ color: 'rgba(245, 245, 245, 0.48)' }}>
                                        Powered by <span style={{ color: C.gold }}>SLAM Sports AI</span>
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
});

function GoogleLogo() {
    return (
        <svg width="18" height="18" viewBox="0 0 48 48">
            <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
            <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
            <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
            <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
        </svg>
    );
}

export default Login;
